import React from "react";
import {
  MapPin,
  FileText,
  Image as ImageIcon,
  Video,
  ExternalLink,
  ChevronRight,
  Navigation,
} from "lucide-react";
import { motion } from "framer-motion";
import WhatsAppFormattedText from "./WhatsAppFormattedText";

export default function TemplatePreview({ tpl, contactName = "", onButtonClick = null }) {
  if (!tpl) return null;
  const header = tpl.components.find((c) => c.type === "HEADER");
  const bodyC = tpl.components.find((c) => c.type === "BODY");
  const footer = tpl.components.find((c) => c.type === "FOOTER")?.text;
  const buttons =
    tpl.components.find((c) => c.type === "BUTTONS")?.buttons || [];

  const renderHeader = () => {
    if (!header) return null;
    const fmt = header.format;
    if (fmt === "IMAGE") {
      const url = header.example?.header_handle?.[0];
      if (!url)
        return (
          <div className="w-full aspect-video bg-slate-50 rounded-[1.5rem] mb-3 flex flex-col items-center justify-center gap-2 border border-dashed border-slate-200">
            <ImageIcon className="w-8 h-8 text-slate-300" />
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
              Imagen de Cabecera
            </span>
          </div>
        );
      return (
        <img
          src={url}
          className="w-full rounded-[1.5rem] mb-3 shadow-sm border border-white"
          alt="header"
        />
      );
    }
    if (fmt === "TEXT" && header.text) {
      let headText = header.text;
      if (tpl.variableValues) {
        Object.entries(tpl.variableValues).forEach(([key, value]) => {
          if (key.startsWith("header_")) {
            const realKey = key.replace("header_", "");
            headText = headText.replace(
              new RegExp(`{{\\s*${realKey}\\s*}}`, "g"),
              value || `{{${realKey}}}`,
            );
          } else if (!tpl.variableValues[`header_${key}`]) {
            // Fallback para cuando no hay prefijo
            headText = headText.replace(
              new RegExp(`{{\\s*${key}\\s*}}`, "g"),
              value || `{{${key}}}`,
            );
          }
        });
      }

      return (
        <div className="text-sm font-black text-slate-900 mb-3 whitespace-pre-wrap uppercase tracking-tight">
          {headText}
        </div>
      );
    }
    if (fmt === "VIDEO") {
      // Igual que IMAGE, la URL del vídeo viaja en example.header_handle[0]. Solo se
      // reproduce si es una URL http: si no, es el handle de Meta o el "PLACEHOLDER" de
      // la plantilla recién creada (ContactTemplateModal pinta la plantilla cruda de
      // GET /plantillas) y saldría un reproductor roto en vez de la tarjeta.
      const url = header.example?.header_handle?.[0];
      if (!url || !/^https?:\/\//i.test(url))
        return (
          <div className="mb-3 p-6 rounded-[1.5rem] bg-gradient-to-br from-red-500/5 to-red-600/5 border border-red-100 flex flex-col items-center gap-3">
            <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center shadow-sm text-red-500">
              <Video className="w-6 h-6" />
            </div>
            <span className="text-[10px] font-black text-red-400 uppercase tracking-widest">
              Vídeo adjunto
            </span>
          </div>
        );
      return (
        <video
          src={url}
          controls
          playsInline
          preload="metadata"
          className="w-full rounded-[1.5rem] mb-3 shadow-sm border border-white bg-black"
        />
      );
    }
    if (fmt === "DOCUMENT") {
      return (
        <div className="mb-3 p-4 rounded-[1.5rem] bg-slate-50 border border-slate-100 flex items-center gap-4">
          <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center shadow-sm text-emerald-500">
            <FileText className="w-6 h-6" />
          </div>
          <div className="flex flex-col">
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
              Documento adjunto
            </span>
            <span className="text-sm font-bold text-slate-700">
              archivo.pdf
            </span>
          </div>
        </div>
      );
    }
    if (fmt === "LOCATION") {
      const loc = tpl.extras?.header_location || tpl._meta?.location || {};
      return (
        <div className="mb-3 p-4 rounded-[1.5rem] bg-slate-50 border border-slate-100 relative overflow-hidden group">
          <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:scale-110 transition-transform">
            <Navigation className="w-12 h-12 text-rose-500" />
          </div>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm text-rose-500">
              <MapPin className="w-5 h-5" />
            </div>
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
              Ubicación
            </span>
          </div>
          <div className="relative z-10">
            {loc.name && (
              <div className="text-sm font-bold text-slate-800">{loc.name}</div>
            )}
            {loc.address && (
              <div className="text-xs font-medium text-slate-500 mt-0.5">
                {loc.address}
              </div>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  const renderBody = () => {
    if (!bodyC?.text) return null;
    let body = bodyC.text;

    if (tpl.variableValues) {
      Object.entries(tpl.variableValues).forEach(([key, value]) => {
        if (key.startsWith("body_")) {
          const realKey = key.replace("body_", "");
          body = body.replace(
            new RegExp(`{{\\s*${realKey}\\s*}}`, "g"),
            value || `{{${realKey}}}`,
          );
        } else if (!key.startsWith("header_")) {
          // Si no tiene prefijo y no es de cabecera, lo tratamos como body (fallback)
          body = body.replace(
            new RegExp(`{{\\s*${key}\\s*}}`, "g"),
            value || `{{${key}}}`,
          );
        }
      });
    } else {
      if (tpl.parameter_format === "NAMED") {
        const examples = bodyC.example?.body_text_named_params || [];
        examples.forEach(({ param_name, example }) => {
          const repl =
            param_name === "nombre"
              ? contactName || "{nombre}"
              : example || `{${param_name}}`;
          body = body.replace(
            new RegExp(`{{\\s*${param_name}\\s*}}`, "g"),
            repl,
          );
        });
      }
      if (tpl.parameter_format === "POSITIONAL") {
        const params = tpl.preview_params || [];
        body = body.replace(/{{\s*(\d+)\s*}}/g, (_, idx) => {
          const i = Number(idx) - 1;
          return params[i] ?? `{{${idx}}}`;
        });
      }
    }

    return (
      <p className="text-sm font-medium text-slate-700 whitespace-pre-wrap leading-relaxed">
        {body.split(/({{[^}]+}})/g).map((part, i) =>
          part.startsWith("{{") ? (
            <span
              key={i}
              className="px-1.5 py-0.5 bg-red-50 text-red-600 rounded-md font-bold text-[11px] align-middle"
            >
              {part}
            </span>
          ) : (
            <WhatsAppFormattedText key={i} text={part} />
          ),
        )}
      </p>
    );
  };

  const renderButtons = () => {
    if (!buttons.length) return null;
    const isClickable = typeof onButtonClick === "function";
    return (
      <div className="mt-4 space-y-2">
        {buttons.map((b, i) => {
          const isFlow = (b.type || b.sub_type) === "FLOW" || (b.type || b.sub_type) === "flow";
          const clickable = isClickable && isFlow;
          return (
            <button
              type="button"
              key={i}
              onClick={clickable ? () => onButtonClick(b, i) : undefined}
              className={`w-full flex items-center justify-between p-4 bg-white border rounded-2xl transition-all text-left group ${
                clickable
                  ? "border-emerald-200 hover:bg-emerald-50 hover:shadow-md cursor-pointer active:scale-[0.99]"
                  : "border-slate-100 hover:bg-slate-50 hover:shadow-md cursor-default"
              }`}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`w-8 h-8 rounded-xl flex items-center justify-center transition-colors ${
                    clickable
                      ? "bg-emerald-50 text-emerald-600 group-hover:bg-white"
                      : "bg-red-50 text-red-500 group-hover:bg-white"
                  }`}
                >
                  {b.type === "URL" ? (
                    <ExternalLink className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </div>
                <div className="flex flex-col">
                  <span className="text-xs font-bold text-slate-700">
                    {b.text}
                  </span>
                  {b.url && (
                    <span className="text-[9px] font-medium text-slate-400 truncate max-w-[200px]">
                      {b.url}
                    </span>
                  )}
                  {clickable && (
                    <span className="text-[9px] font-bold text-emerald-500 uppercase tracking-wider">
                      Pulsa para ver el formulario
                    </span>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div className="p-4 sm:p-8 rounded-[2rem] sm:rounded-[2.5rem] bg-slate-50 border border-slate-200/50 shadow-inner relative overflow-hidden group">
      {/* Abstract Background Elements */}
      <div className="absolute top-0 left-0 w-32 h-32 bg-red-500/5 rounded-full -translate-x-1/2 -translate-y-1/2 blur-2xl" />
      <div className="absolute bottom-0 right-0 w-32 h-32 bg-red-500/5 rounded-full translate-x-1/2 translate-y-1/2 blur-2xl" />

      <div className="relative">
        <div className="flex items-center justify-center mb-6">
          <div className="px-4 py-1.5 bg-white/80 backdrop-blur-sm rounded-full border border-white shadow-sm">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">
              Vista Previa WhatsApp
            </p>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-[2rem] border border-white shadow-xl shadow-slate-200/50 p-6 max-w-sm mx-auto"
        >
          {renderHeader()}
          <div className="space-y-4">
            {renderBody()}
            {footer && (
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider py-2 border-t border-slate-50">
                {footer}
              </p>
            )}
          </div>
          {renderButtons()}
        </motion.div>
      </div>
    </div>
  );
}
